import classNames from "classnames";
import {
    KeyboardBackspaceIcon,
    KeyboardSpacebarIcon,
    ShortcutArrowIcon,
    ShortcutEnterIcon,
    ShortcutShiftIcon,
} from "@icons";
import type {
    ShortcutIconName,
    ShortcutKey as ShortcutKeyData,
} from "./formatShortcut";
import styles from "./Shortcut.module.scss";

type TextKind = Exclude<ShortcutKeyData["kind"], "icon">;

const textKindClassNames: Record<TextKind, string> = {
    letter: styles.letter,
    text: styles.text,
    symbol: styles.symbol,
    equal: styles.equal,
};

const icons: Record<
    ShortcutIconName,
    { Icon: typeof ShortcutArrowIcon; className?: string }
> = {
    enter: { Icon: ShortcutEnterIcon },
    shift: { Icon: ShortcutShiftIcon },
    "arrow-down": { Icon: ShortcutArrowIcon },
    "arrow-left": { Icon: ShortcutArrowIcon, className: styles.arrowLeft },
    "arrow-up": { Icon: ShortcutArrowIcon, className: styles.arrowUp },
    "arrow-right": { Icon: ShortcutArrowIcon, className: styles.arrowRight },
    backspace: { Icon: KeyboardBackspaceIcon },
    space: { Icon: KeyboardSpacebarIcon },
};

/**
 * One key chip. Letters and words read as their text; symbol and SVG keys
 * carry the key name as their accessible label.
 */
export const ShortcutKey = ({
    shortcutKey,
}: {
    shortcutKey: ShortcutKeyData;
}) => {
    if (shortcutKey.kind === "icon") {
        const { Icon, className } = icons[shortcutKey.icon];

        return (
            <kbd className={styles.key} aria-label={shortcutKey.name}>
                <Icon
                    aria-hidden="true"
                    className={classNames(styles.glyph, className)}
                />
            </kbd>
        );
    }

    return (
        <kbd
            className={classNames(
                styles.key,
                textKindClassNames[shortcutKey.kind],
            )}
            aria-label={
                shortcutKey.kind === "symbol" ? shortcutKey.name : undefined
            }
        >
            {shortcutKey.label}
        </kbd>
    );
};
